// @ts-strict-ignore
import React, { useEffect } from 'react'
import { useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Box, Callout, Combobox, Radio } from '@node-space/ui-components'
import { exportFormatRadioOptions } from 'components/export/onceOff/constants/exportOptions'
import { daysOfWeek, timesOfDay } from 'constants/transactionsExport'
import { useRecurringExportContext } from 'hooks/context/useRecurringExportContext'
import { useReportScheduleTimezonesQuery } from 'hooks/queries/useReportScheduleTimezonesQuery'
import {
  ExportFileFormatType,
  ReportScheduleFrequency,
  ReportScheduleFrequencyType,
} from 'types/transactionsExport'
import { getScheduleErrorMessage } from 'utils/export/exportErrorMessages'
import { getUserDefaultTimezone } from 'utils/transactionsExports'

interface RecurringExportFormType {
  downloadAll: boolean
}

export const RecurringExportForm = ({ downloadAll }: RecurringExportFormType) => {
  const { t } = useTranslation()

  const {
    form,
    isErrorPostReportSchedule,
    postReportScheduleError,
    isUpdatingReportSchedule,
    currentReportSchedule,
    isErrorPutReportSchedule,
    putReportScheduleError,
  } = useRecurringExportContext()

  const exportFormatOptions = exportFormatRadioOptions()

  const errorMessage = getScheduleErrorMessage(
    postReportScheduleError,
    putReportScheduleError,
    isUpdatingReportSchedule,
    t
  )

  const {
    control,
    setValue,
    register,
    clearErrors,
    formState: { errors },
  } = form
  const formValues = useWatch({ control })

  const { data: timezones } = useReportScheduleTimezonesQuery(true)

  const timezoneOptions = timezones?.map(timezone => ({
    leftText: timezone?.label,
    value: timezone?.zoneId,
  }))

  useEffect(() => {
    if (isUpdatingReportSchedule) {
      setValue('frequencyType', currentReportSchedule?.frequencyType)
      setValue('frequencyDay', String(currentReportSchedule?.frequencyDay))
      setValue('time', currentReportSchedule?.time)
      setValue('timezone', currentReportSchedule?.timezone)
      setValue('format', currentReportSchedule?.format)
    } else {
      const userDefaultTimezone = getUserDefaultTimezone(timezones)
      setValue('timezone', userDefaultTimezone?.zoneId)
    }
  }, [isUpdatingReportSchedule, timezones])

  return (
    <Box flexCol gapY={16} tag="form">
      <Radio
        {...register('frequencyType')}
        onChange={value => {
          setValue('frequencyType', value as ReportScheduleFrequencyType)
        }}
        value={formValues?.frequencyType}
        horizontal
        options={[
          { value: ReportScheduleFrequency.DAILY, label: t('exports.daily') },
          { value: ReportScheduleFrequency.WEEKLY, label: t('exports.weekly') },
          { value: ReportScheduleFrequency.MONTHLY, label: t('exports.monthly') },
        ]}
      />

      {formValues?.frequencyType === ReportScheduleFrequency.WEEKLY && (
        <Combobox
          {...register('frequencyDay')}
          label={t('exports.dayOfTheWeek')}
          defaultValue={formValues.frequencyDay}
          id="dayOfTheWeek"
          items={daysOfWeek}
          onChange={value => {
            setValue('frequencyDay', value as ReportScheduleFrequencyType)
          }}
          errorText={errors?.frequencyDay?.message}
          modal
        />
      )}

      <Box flex width="full" gapX={8}>
        <Box className="flex-1">
          <Combobox
            {...register('time')}
            label={t('exports.sendAt')}
            defaultValue={formValues.time}
            id="sendAt"
            testId="sendAt"
            items={timesOfDay}
            onChange={value => {
              clearErrors('time')
              setValue('time', value)
            }}
            errorText={errors?.time?.message}
            modal
          />
        </Box>

        <Box className="flex-[2_1_0%]">
          <Combobox
            {...register('timezone')}
            label={t('exports.timezone')}
            key={`timezone-${formValues.timezone}`}
            defaultValue={formValues.timezone}
            id="timezone"
            testId="timezone"
            items={timezoneOptions}
            onChange={value => {
              clearErrors('timezone')
              setValue('timezone', value)
            }}
            errorText={errors?.timezone?.message}
            modal
          />
        </Box>
      </Box>

      {!downloadAll && (
        <Box paddingT={4}>
          <p className="mb-1 text-label text-foreground-prominent">
            {t('walletsPage.exportFormat')}
          </p>
          <Radio
            {...register('format')}
            horizontal
            onChange={value => {
              setValue('format', value as ExportFileFormatType)
            }}
            options={exportFormatOptions}
            value={formValues?.format?.toLowerCase()}
            testId="format"
          />
        </Box>
      )}

      {(isErrorPostReportSchedule || isErrorPutReportSchedule) && (
        <Callout variant="error">{errorMessage}</Callout>
      )}
    </Box>
  )
}
